// Stream Deck (WebHID) progressive enhancement for the Go Live surface.
//
// WebHID is Chromium-only and needs a secure context + a user gesture, so every
// caller feature-detects with `hidSupported()` and drives the chooser from a
// click. The @elgato-stream-deck/webhid library is imported dynamically (see
// `openStreamDeck`) so it stays out of the initial bundle and is easy to mock.

import type {
  StreamDeck,
  StreamDeckButtonControlDefinition,
  StreamDeckEncoderControlDefinition,
} from "@elgato-stream-deck/webhid";

/** An opened Stream Deck device handle. */
export type Deck = StreamDeck;

/** The control passed to the deck's `down`/`up` events. */
export type DeckControl = StreamDeckButtonControlDefinition | StreamDeckEncoderControlDefinition;

/** True when the browser exposes WebHID (Chromium + secure context). */
export function hidSupported(): boolean {
  return typeof navigator !== "undefined" && "hid" in navigator;
}

/**
 * Open the browser's Stream Deck chooser and return the first selected device,
 * or null when the user cancels (an empty selection). Must be called from a
 * user gesture. The library is loaded lazily so it never bloats the bundle.
 */
export async function openStreamDeck(): Promise<Deck | null> {
  const mod = await import("@elgato-stream-deck/webhid");
  const decks = await mod.requestStreamDecks();
  return decks[0] ?? null;
}

/**
 * Turn a Stream Deck / WebHID failure into a message a broadcaster can act on.
 *
 * The single most common desktop cause is that the native **Elgato Stream Deck
 * app** (or another browser tab) already holds the device, so the browser's
 * `HIDDevice.open()` is refused — we lead with that hint. We always append the
 * underlying error text so an unusual failure is still diagnosable from the UI
 * (the caller also logs the raw error to the console). A user-cancelled chooser
 * never reaches here — the caller treats NotFoundError as a no-op.
 */
export function describeDeckError(err: unknown): string {
  const raw = err instanceof Error ? err.message : typeof err === "string" ? err : String(err ?? "");
  const low = raw.toLowerCase();
  if (
    low.includes("open") ||
    low.includes("in use") ||
    low.includes("busy") ||
    low.includes("claim") ||
    low.includes("access")
  ) {
    return `Couldn't open the Stream Deck — quit the Elgato Stream Deck desktop app (it holds the device), then reconnect.${raw ? ` [${raw}]` : ""}`;
  }
  if (low.includes("unexpected type") || low.includes("not supported")) {
    return `This Stream Deck model isn't supported yet.${raw ? ` [${raw}]` : ""}`;
  }
  return raw ? `Stream Deck error: ${raw}` : "Stream Deck unavailable";
}

// Solid per-action LED colors (r, g, b) — physical key fills, not CSS, so raw
// RGB is intentional here rather than design tokens.
export const KEY_COLORS = {
  goLive: [22, 163, 74], // green — start broadcast
  stop: [200, 40, 40], // red — stop broadcast
  micOn: [55, 65, 81], // slate — mic live
  micMuted: [202, 138, 4], // amber — mic muted
} as const;

/** Key index of the primary action (go live / stop). */
export const KEY_PRIMARY = 0;
/** Key index of the mic mute toggle. */
export const KEY_MUTE = 1;
